export const DEFAULT_BACKGROUND_FIT = "fill";
export const DEFAULT_OVERLAY = 40;

export const BG_FIT_OPTIONS = [
  { value: "fill", label: "Fill" },
  { value: "fit", label: "Fit" },
  { value: "stretch", label: "Stretch" },
  { value: "center", label: "Center" },
  { value: "tile", label: "Tile" },
];

const FIT_ALIASES = {
  fill: "fill",
  regular: "fill",
  reguer: "fill",
  cover: "fill",
  default: "fill",
  fit: "fit",
  contain: "fit",
  stretch: "stretch",
  center: "center",
  tile: "tile",
  repeat: "tile",
};

const clampOverlay = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_OVERLAY;
  return Math.min(100, Math.max(0, Math.round(numeric)));
};

export const normalizeBackgroundFitValue = (value) => {
  const key = String(value ?? "")
    .trim()
    .toLowerCase();
  return FIT_ALIASES[key] || DEFAULT_BACKGROUND_FIT;
};

export const backgroundFitLabel = (value) => {
  const normalized = normalizeBackgroundFitValue(value);
  return (
    BG_FIT_OPTIONS.find((option) => option.value === normalized)?.label || "Fill"
  );
};

export const getBackgroundStyles = (url, fit = DEFAULT_BACKGROUND_FIT) => {
  const base = { backgroundImage: url ? `url(${JSON.stringify(url)})` : "none" };
  switch (normalizeBackgroundFitValue(fit)) {
    case "fit":
      return {
        ...base,
        backgroundSize: "contain",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
    case "stretch":
      return {
        ...base,
        backgroundSize: "100% 100%",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
    case "center":
      return {
        ...base,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
    case "tile":
      return {
        ...base,
        backgroundSize: "auto",
        backgroundPosition: "top left",
        backgroundRepeat: "repeat",
      };
    default:
      return {
        ...base,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
  }
};

export const normalizeThemeConfigEntry = (source = {}) => ({
  BackgroundFit: backgroundFitLabel(
    source.BackgroundFit ?? source.backgroundFit,
  ),
  Overlay: clampOverlay(
    source.Overlay ?? source.overlay ?? DEFAULT_OVERLAY,
  ),
});

const themeConfigItemKey = (item) => {
  if (!item || typeof item !== "object") return "";
  return String(Object.keys(item)[0] ?? "").trim();
};

const toThemeConfigItem = (fileName, entry) => {
  const name = String(fileName ?? "").trim();
  if (!name) return null;
  return { [name]: normalizeThemeConfigEntry(entry) };
};

export const parseThemeConfig = (raw) => {
  if (raw == null || raw === "") return [];

  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    parsed = Object.entries(parsed).map(([fileName, entry]) => ({
      [fileName]: entry,
    }));
  }

  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((item) => {
      const fileName = themeConfigItemKey(item);
      if (!fileName) return null;
      return toThemeConfigItem(fileName, item[fileName]);
    })
    .filter(Boolean);
};

export const serializeThemeConfig = (themeConfig) =>
  JSON.stringify(Array.isArray(themeConfig) ? themeConfig : []);

export const getThemeConfigForFile = (themeConfig, fileName) => {
  const name = String(fileName ?? "").trim().toLowerCase();
  if (!name) return normalizeThemeConfigEntry();

  const list = Array.isArray(themeConfig) ? themeConfig : [];
  const match = list.find(
    (item) => themeConfigItemKey(item).toLowerCase() === name,
  );
  const fileKey = themeConfigItemKey(match);
  return normalizeThemeConfigEntry(fileKey ? match[fileKey] : {});
};

export const upsertThemeConfig = (themeConfig, fileName, patch = {}) => {
  const name = String(fileName ?? "").trim();
  if (!name) return Array.isArray(themeConfig) ? themeConfig : [];

  const current = getThemeConfigForFile(themeConfig, name);
  const nextEntry = normalizeThemeConfigEntry({
    ...current,
    ...patch,
    BackgroundFit: patch.BackgroundFit ?? current.BackgroundFit,
    Overlay: patch.Overlay ?? current.Overlay,
  });

  const list = Array.isArray(themeConfig) ? [...themeConfig] : [];
  const index = list.findIndex(
    (item) => themeConfigItemKey(item).toLowerCase() === name.toLowerCase(),
  );
  const item = { [name]: nextEntry };

  if (index >= 0) {
    list[index] = item;
    return list;
  }
  return [...list, item];
};

export const pruneThemeConfig = (themeConfig, allowedNames) => {
  const allowed = new Set(
    (Array.isArray(allowedNames) ? allowedNames : [])
      .map((name) => String(name ?? "").trim().toLowerCase())
      .filter(Boolean),
  );
  return (Array.isArray(themeConfig) ? themeConfig : []).filter((item) =>
    allowed.has(themeConfigItemKey(item).toLowerCase()),
  );
};

export const removeThemeConfigEntries = (themeConfig, namesToRemove) => {
  const removed = new Set(
    (Array.isArray(namesToRemove) ? namesToRemove : [namesToRemove])
      .map((name) => String(name ?? "").trim().toLowerCase())
      .filter(Boolean),
  );
  return (Array.isArray(themeConfig) ? themeConfig : []).filter(
    (item) => !removed.has(themeConfigItemKey(item).toLowerCase()),
  );
};

export const buildCompleteThemeConfig = (themeConfig, fileNames) => {
  const names = (Array.isArray(fileNames) ? fileNames : [])
    .map((name) => String(name ?? "").trim())
    .filter(Boolean);

  let next = pruneThemeConfig(themeConfig, names);
  names.forEach((name) => {
    if (
      next.some(
        (item) => themeConfigItemKey(item).toLowerCase() === name.toLowerCase(),
      )
    ) {
      return;
    }
    next = upsertThemeConfig(next, name);
  });
  return next;
};

export const getPreviewSettings = (entry) => {
  const overlay = clampOverlay(entry?.Overlay);
  return {
    backgroundFit: normalizeBackgroundFitValue(entry?.BackgroundFit),
    overlayEnabled: overlay > 0,
    overlayOpacity: overlay > 0 ? overlay : DEFAULT_OVERLAY,
  };
};

export const enabledOverlayOpacity = (opacity) => {
  const numeric = clampOverlay(opacity);
  return numeric > 0 ? numeric : DEFAULT_OVERLAY;
};

export const configPatchFromPreview = ({
  backgroundFit,
  overlayEnabled,
  overlayOpacity,
} = {}) => ({
  BackgroundFit: backgroundFitLabel(backgroundFit),
  Overlay: overlayEnabled ? clampOverlay(overlayOpacity) : 0,
});
